// Settings › Appeals: the appeal window per payer, in days (D229). In-memory mock.
// Seeded from the payer rules (all researched windows are 180 days in the mock). A practice
// can change a payer's number; denied and underpaid deadlines then use it.
import { useSyncExternalStore } from 'react';
import { PAYER_PLAN_RULES, ruleForPayerName } from './payerRules';
import type { Deadline } from './types';
import { addDays } from './utils';

export const MIN_WINDOW_DAYS = 1;
export const MAX_WINDOW_DAYS = 730;
/** The window every fixture deadline was written with. */
const FIXTURE_WINDOW_DAYS = 180;

/** What ELVA aims for when a payer's window isn't confirmed (D241/D256). Never the payer's deadline. */
export const AIM_DAYS = 30;

export interface AppealWindow {
  days: number;
  /** True once the practice changed it. */
  setByPractice: boolean;
  /** False when the payer's window isn't researched; then `days` is ELVA's aim (30), not a deadline. */
  confirmed: boolean;
}

function seedDays(deadlineBasis: string): number {
  const m = /^(\d+)\s+days?/.exec(deadlineBasis);
  return m ? Number(m[1]) : FIXTURE_WINDOW_DAYS;
}

function seed(): Record<string, AppealWindow> {
  const out: Record<string, AppealWindow> = {};
  for (const r of PAYER_PLAN_RULES) {
    const confirmed = r.windowConfirmed ?? r.source.startsWith('From ');
    out[r.key] = { days: confirmed ? seedDays(r.deadlineBasis) : AIM_DAYS, setByPractice: false, confirmed };
  }
  return out;
}

let windows = seed();
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getAppealWindow(key: string): AppealWindow {
  return windows[key] ?? { days: FIXTURE_WINDOW_DAYS, setByPractice: false, confirmed: true };
}

export function setAppealWindow(key: string, days: number): void {
  const clamped = Math.max(MIN_WINDOW_DAYS, Math.min(MAX_WINDOW_DAYS, Math.round(days)));
  // Once the practice sets a number it is the practice's own deadline, confirmed or not.
  windows = { ...windows, [key]: { days: clamped, setByPractice: true, confirmed: windows[key]?.confirmed ?? true } };
  for (const l of listeners) l();
}

/** Re-render when any window changes. */
export function useAppealWindows(): Record<string, AppealWindow> {
  return useSyncExternalStore(subscribe, () => windows, () => windows);
}

export function daysWord(n: number): string {
  return `${String(n)} ${n === 1 ? 'day' : 'days'}`;
}

/**
 * A deadline with the practice's window applied: the date moves with the window and the
 * sentence reads "Aetna allows 180 days from its decision". With the default windows the
 * result is the fixture's own deadline.
 */
export function withAppealWindow(
  d: Deadline,
  payer: { name: string; short: string },
  fallbackAnchor: 'decision' | 'payment' = 'decision',
): Deadline {
  const w = getAppealWindow(ruleForPayerName(payer.name).key);
  const days = w.days;
  const anchor = d.basis.includes('payment') ? 'payment' : d.basis.includes('decision') ? 'decision' : fallbackAnchor;
  const date = addDays(d.date, days - FIXTURE_WINDOW_DAYS);
  if (!w.confirmed && !w.setByPractice) {
    // Not researched: ELVA's own aim, never shown as the payer's deadline (D241/D256).
    return { ...d, date, basis: `${payer.short}’s window isn’t confirmed yet`, aim: true };
  }
  if (w.setByPractice && !w.confirmed) {
    // The practice gave an unconfirmed payer a number: it reads as the practice's own deadline.
    return { ...d, date, basis: `Your practice set ${daysWord(days)} from the ${anchor}` };
  }
  return { ...d, date, basis: `${payer.short} allows ${daysWord(days)} from its ${anchor}` };
}

/** "Appeal by" / "Ask by", or "ELVA aims to send by" / "ELVA aims to ask by" when the window isn't confirmed. */
export function deadlineLead(d: Deadline): string {
  if (!d.aim) return `${d.verb} by`;
  return d.verb === 'Ask' ? 'ELVA aims to ask by' : 'ELVA aims to send by';
}
